import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// internal hooks
import { useEscape } from "../hooks/useEscape";

// styles and images
import "./Components.css";
import Close from "../assets/close.svg";

// store
import {
  messageAndErrorStore,
  userStore,
  selectOptionsStore,
  companyStore,
} from "../store/Store";
import useGetFetch from "../hooks/useGetFetch";

export default function DeletePopup({
  setTrigger,
  id,
  table,
  endpoint,
  nameField,
  projectId,
  setDocId,
  setStates,
  setStateFields,
  setState,
}) {
  // global state
  const JwtToken = userStore((userDataStore) => userDataStore.JwtToken);
  const currentUser = userStore((userDataStore) => userDataStore.currentUser);
  const setCompanyCarriers = companyStore((data) => data.setCompanyCarriers);
  const setCarrierDrivers = companyStore((data) => data.setCarrierDrivers);
  const carrierOptions = selectOptionsStore((data) => data.carrierOptions);
  const setAuthError = messageAndErrorStore((data) => data.setAuthError);
  const toggleNewMessage = messageAndErrorStore((data) => data.toggleNewMessage);

  // state
  const [name, setName] = useState(null);
  const [body, setBody] = useState({
    id,
    table,
    updated_by_id: 0,
    project_id: projectId,
    vendor_name: "",
    purchase_order: "",
  });
  const [pending, setPending] = useState(false);

  const navigate = useNavigate();

  const {
    theData,
    isPending,
    error: recordError,
  } = useGetFetch(`${process.env.REACT_APP_BACKEND}/pc/${endpoint}/${id}`);

  useEscape(setTrigger, [setDocId], [""], pending);
  const handleClose = () => {
    setDocId("");
    setTrigger(false);
  };

  useEffect(() => {
    if (theData) {
      switch (endpoint) {
        case "rate-sheet":
          setName(theData.rate_sheet.rs_name);
          break;
        case "asset-rate-sheet":
          setName(theData.rate_sheet.rs_name);
          break;
        case "fsc-rate-sheet":
          setName(theData.fsc_rate_sheet.fsc_name);
          break;
        case "project/carrier":
          setName(nameField);
          if (table === "project_carriers_tbl") {
            const carr = carrierOptions.filter(
              (x) => x.value === theData.pc_carrier_id,
            );
            if (carr.length > 0) {
              setBody((prev) => ({
                ...prev,
                vendor_name: carr[0].label,
              }));
            }
          }
          break;
        case "project/sand":
          setBody((prev) => ({
            ...prev,
            purchase_order: theData.po_number,
          }));
          setName(theData[nameField]);
          break;
        case "account-customer-invoices":
          setName(theData.customer_invoice[nameField]);
          break;
        case "vendor/invoice":
          setName(theData.invoice[nameField]);
          break;
        case "customer/invoice":
          setName(theData.invoice[nameField]);
          break;
        case "company-list":
          setName(theData.company_list[nameField]);
          break;
        case "credits-debits":
          if (theData.is_credit) {
            setName("Credit")
          } else {
            setName("Debit")
          }
          break
        default:
          setName(theData[nameField]);
          break;
      }
    }
  }, [theData, endpoint, nameField, setName, carrierOptions, table]);
  
  const handleClick = () => {
    setPending(true);
    if (!currentUser) {
      toggleNewMessage("You are not logged in");
      return;
    }
    const headers = new Headers();
    headers.append("Content-Type", "application/json");
    headers.append("Authorization", "Bearer " + JwtToken);
    if (table === "company_carriers_tbl" || table === "company_drivers_tbl") {
      // create body
      let body;
      if (table === "company_carriers_tbl") {
        body = {
          user_id: currentUser.id,
          table_name: table,
          carrier_id: id,
        };
      } else {
        body = {
          user_id: currentUser.id,
          table_name: table,
          carrier_id: theData.carrier_id,
        };
      }
      const controller = new AbortController();
      const requestOptions = {
        method: "DELETE",
        headers,
        body: JSON.stringify(body),
        signal: controller.signal,
      };

      fetch(
        `${process.env.REACT_APP_BACKEND}/pc/delete-driver-carrier/${id}`,
        requestOptions,
      )
        .then((res) => {
          if (!res.ok) {
            if (res.status === 401) {
              navigate("/login");
              setAuthError(true);
              setTrigger(false);
              setPending(false);
              return Promise.resolve();
            } else {
              toggleNewMessage(`Something went wrong - ${res.status}`);
              setPending(false);
              return Promise.resolve();
            }
          } else {
            return res.json();
          }
        })
        .then((data) => {
          setCarrierDrivers(data.carrier_drivers);
          setCompanyCarriers(data.company_carriers);
          setPending(false);
          setTrigger(false);
          setDocId("");
        })
        .catch((_) => {
          toggleNewMessage("Something went wrong");
          return;
        })
        .finally(() => setPending(false));
      return () => {
        controller.abort();
      };
    } else {
      const controller = new AbortController();
      var reqBody = body;
      reqBody.updated_by_id = currentUser.id;
      const requestOptions = {
        method: "DELETE",
        headers,
        body: JSON.stringify(body),
        signal: controller.signal,
      };
      var deleteUrl = `${process.env.REACT_APP_BACKEND}/pc/delete-record`
      if (table === 'project_vendor_invoice_tbl') {
        deleteUrl = `${process.env.REACT_APP_BACKEND}/pc/delete-record?is_received=false`
      }
      fetch(deleteUrl, requestOptions)
        .then((res) => {
          if (res.status === 401) {
            navigate("/login");
            setAuthError(true);
            setTrigger(false);
            setPending(false);
            return Promise.resolve(); // Resolved to prevent catch block
          } else if (res.status === 409) {
            if (table === "users_tbl") {
              toggleNewMessage("This User Cannot be Deleted!");
            } else {
              toggleNewMessage(
                "Record cannot be deleted because it is being used in a Project",
              );
            }
            setPending(false);
            return Promise.resolve(); // Resolved to prevent catch block
          } else {
            return res.json();
          }
        })
        .then((data) => {
          if (!data) {
            if (setState) {
              setState(data);
              setDocId("")
              return;
            } else if (setStates) {
              setStates.forEach((s, idx) => {
                s(data[setStateFields[idx]]);
              });
              setDocId("")
              setTrigger(false);
              return;
            }
          }
          if (data.error) {
            if (data.message === "invoice exists") {
              if (table === "project_carriers_tbl") {
                toggleNewMessage(
                  "An invoice from this carrier exists, please remove before deleting!",
                );
              } else if (table === "project_sand_rates_tbl") {
                toggleNewMessage(
                  "An invoice from this purchase order exists, please remove before deleting!",
                );
              }
              setPending(false);
              return;
            } else {
              toggleNewMessage(data.message);
              setPending(false);
              setDocId("")
              return;
            }
          } else {
            if (setState) {
              setState(data);
            } else if (setStates) {
              setStates.forEach((s, idx) => {
                s(data[setStateFields[idx]]);
              });
            }
            setPending(false);
            setTrigger(false);
            setDocId("")
          }
        })
        .catch((err) => {
          setPending(false);
          console.log(err)
          toggleNewMessage("Could not delete the record");
        })
        .finally(() => {
          setPending(false);
          setTrigger(false);
        });
      return () => {
        controller.abort();
      };
    }
  };

  const handleNo = () => {
    setTrigger(false);
    setDocId("");
  };

  return (
    !isPending && (
      <div className="popup">
        <div className="popup-inner-add-company">
          {!pending && <img src={Close} onClick={handleClose} alt="close" />}
          {theData && !recordError && !isPending && (
            <p>{`Are you sure you want to delete ${name}?`}</p>
          )}
          {!pending && (
            <button className="delete-btn" onClick={handleClick}>
              Yes
            </button>
          )}
          {!pending && (
            <button className="propx-btn" onClick={handleNo}>
              No
            </button>
          )}
          {pending && (
            <button
              className="btn btn-secondary"
              style={{ margin: "20px 20px 20px 0" }}
              disabled
            >
              Submitting...
              <span
                className="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              ></span>
            </button>
          )}
        </div>
      </div>
    )
  );
}
